import { api } from './api'
import { SubmissionList, EnhancedPublicSubmissionData } from '../types/unified_models'

export interface SubmissionHistoryService {
  getSubmissionHistory: (problemId: string) => Promise<SubmissionList>
  getSubmissionData: (submissionId: string) => Promise<EnhancedPublicSubmissionData>
  updateSubmissionName: (submissionId: string, name: string) => Promise<void>
  bookmarkSubmission: (submissionId: string, action: 'add' | 'remove') => Promise<void>
}

// Helper function to decode base64
const decodeBase64 = (base64String: string): string => {
  // Since the backend now handles decoding, we can simplify this
  // Just return the string as-is since it should already be decoded
  return base64String
}

export const submissionHistoryService: SubmissionHistoryService = {
  async getSubmissionHistory(problemId: string): Promise<SubmissionList> {
    const response = await api.get(`/get_submission_list/${problemId}`)
    return response.data
  },

  async getSubmissionData(submissionId: string): Promise<EnhancedPublicSubmissionData> {
    const response = await api.get(`/get_submission_data/${submissionId}`)
    const data = response.data
    
    // Decode the base64 encoded source code (if needed)
    if (data.source_code) {
      data.source_code = decodeBase64(data.source_code)
    }
    
    return data
  },

  async updateSubmissionName(submissionId: string, name: string): Promise<void> {
    await api.post('/update_submission_name', {
      submission_id: submissionId,
      name: name
    })
  },

  async bookmarkSubmission(submissionId: string, action: 'add' | 'remove'): Promise<void> {
    await api.get(`/bookmark_submission/${submissionId}/${action}`)
  }
}

// Helper function to get submission name from tags
export const getSubmissionName = (submission: SubmissionList['submission_list'][0]): string => {
  if (!submission.tags) return ''
  
  const nameTag = submission.tags.find(tag => tag.tag_type === 'annotation' && tag.key === 'name')
  return nameTag ? nameTag.value : ''
}

// Helper function to check if submission has a custom name
export const hasCustomName = (submission: SubmissionList['submission_list'][0]): boolean => {
  const name = getSubmissionName(submission)
  return name.length > 0
}

// Helper function to get display name for submission
export const getDisplayName = (submission: SubmissionList['submission_list'][0]): string => {
  const customName = getSubmissionName(submission)
  if (customName) return customName
  
  // Generate a smart default name based on submission characteristics
  const results = submission.submission_results || []
  const timestamp = new Date(submission.time_created)
  
  // Try to create a meaningful default based on results
  const correctnessResult = results.find(r => r.result_type === 'correctness')
  const runtimeResult = results.find(r => r.result_type === 'benchmark' && r.key === 'runtime')
  
  let defaultName = `Submission ${timestamp.toLocaleString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  })}`
  
  // Add performance hint if available
  if (runtimeResult && runtimeResult.value_numeric) {
    const runtime = runtimeResult.value_numeric
    if (runtime < 0.1) defaultName += ' (Fast)'
    else if (runtime > 5.0) defaultName += ' (Slow)'
  }
  
  // Add correctness hint
  if (correctnessResult) {
    const passed = correctnessResult.value === 'passed' || correctnessResult.value === 'success'
    if (!passed) defaultName += ' (Failed)'
  }
  
  return defaultName
}

// Helper function to check if submission is bookmarked
export const isSubmissionBookmarked = (submission: SubmissionList['submission_list'][0]): boolean => {
  if (!submission.tags) return false
  
  return submission.tags.some(tag => tag.tag_type === 'bookmark')
}

// Helper function to get submission performance summary
export const getSubmissionPerformance = (submission: SubmissionList['submission_list'][0]): string => {
  if (!submission.submission_results) return 'No results'
  
  const runtimeResult = submission.submission_results.find(result => result.result_type === 'benchmark' && result.key === 'runtime')
  if (runtimeResult && runtimeResult.value_numeric) {
    return `${runtimeResult.value_numeric.toFixed(3)}s`
  }
  
  const correctnessResult = submission.submission_results.find(result => result.result_type === 'correctness')
  if (correctnessResult) {
    return correctnessResult.value
  }
  
  return submission.status
} 